import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useToast } from 'hooks/use-toast';
import {
  useDeleteOwnToolClient,
  useGetToolConnection,
  useSaveToolCredentials,
} from 'features/chat/agent-chat-module/hooks/use-gpt-chat';

/**
 * The sentence the server gave for a refused request. A failed request carries its body as
 * `{ error: "..." }`, and its `message` is that body as JSON, so the sentence is read
 * from the body; anything else falls back to the caller's own text.
 */
export const serverErrorText = (error: unknown): string => {
  const body = (error as { error?: { error?: unknown } } | null)?.error;
  return typeof body?.error === 'string' ? body.error : '';
};

/** The machine-readable `code` of a failed request's body, read like {@link serverErrorText}. */
export const serverErrorCode = (error: unknown): string => {
  const body = (error as { error?: { code?: unknown } } | null)?.error;
  return typeof body?.code === 'string' ? body.code : '';
};

/** Sign-in start refused because the tool has no app client yet; only its owner can fix it. */
export const TOOL_CLIENT_MISSING = 'tool_client_missing';
/** Sign-in start refused because the user has to bring their own client (the owner allows it). */
export const USER_CLIENT_REQUIRED = 'user_client_required';

interface OwnToolClientSectionProps {
  toolId: string;
  /** The user must enter their own client (the sign-in start said so): fields, no checkbox. */
  required?: boolean;
  /** After the user's own client was saved or removed. */
  onChange?: () => void;
}

/**
 * The user's own client for an API tool whose owner allows one ("Allow users to use their
 * own client"). With a tool client configured, a checkbox "Use my own client instead of the
 * configured one" reveals the Client ID (required) and Client Secret (optional) fields; a
 * saved client shows as checked with its ID masked. Unchecking removes it and signs the user
 * out, so the next sign-in uses the tool's client. Without a tool client, the fields show
 * without the checkbox: there is nothing to override. Values go to the server only; they are
 * never shown in full again or passed to the model. Renders nothing when not allowed.
 */
export function OwnToolClientSection({ toolId, required = false, onChange }: Readonly<OwnToolClientSectionProps>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: connection } = useGetToolConnection(toolId);
  const { mutateAsync: saveToolCredentials, isPending: isSaving } = useSaveToolCredentials();
  const { mutateAsync: deleteOwnToolClient, isPending: isRemoving } = useDeleteOwnToolClient();

  const savedClientId = connection?.ownClientId || '';
  const hasOwn = !!savedClientId;
  const allowed = !!connection?.userClientAllowed;
  const mustEnter = required || (allowed && !connection?.toolClientSet);

  const [checked, setChecked] = useState(hasOwn);
  const [clientId, setClientId] = useState(savedClientId);
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setChecked(hasOwn);
    setClientId(savedClientId);
    setClientSecret('');
    setError('');
  }, [hasOwn, savedClientId]);

  if (!allowed && !required) {
    return null;
  }

  const refetchConnection = () => queryClient.invalidateQueries({ queryKey: ['tools', 'connection', toolId] });

  const save = async () => {
    const id = clientId.trim();
    if (!id) {
      setError(t('CLIENT_ID_REQUIRED') || 'Client ID is required');
      return;
    }
    setError('');
    const secret = clientSecret.trim();
    try {
      // The masked ID as shown, sent back, keeps the saved one; an empty secret keeps the
      // saved secret for the same ID and drops it for a new one.
      const response = await saveToolCredentials({
        toolId,
        payload: secret ? { clientId: id, clientSecret: secret } : { clientId: id },
      });
      if (!response.saved) {
        throw new Error(t('CREDENTIALS_NOT_SAVED') || 'The credentials could not be saved.');
      }
      await refetchConnection();
      toast({
        variant: 'success',
        title: t('CREDENTIALS_SAVED') || 'Credentials saved',
        description: t('OWN_TOOL_CLIENT_SAVED') || 'Your client is saved. Sign in to use it.',
      });
      onChange?.();
    } catch (saveError) {
      toast({
        variant: 'destructive',
        title: t('CREDENTIALS_NOT_SAVED_TITLE') || 'Not saved',
        description:
          serverErrorText(saveError) || t('CREDENTIALS_NOT_SAVED') || 'The credentials could not be saved.',
      });
    }
  };

  const toggle = async (next: boolean) => {
    setChecked(next);
    if (next || !hasOwn) {
      return;
    }
    try {
      await deleteOwnToolClient({ toolId });
      await refetchConnection();
      toast({
        variant: 'success',
        title: t('DISCONNECTED') || 'Disconnected',
        description:
          t('OWN_TOOL_CLIENT_REMOVED') || 'Your client is removed. You sign in through the configured one.',
      });
      onChange?.();
    } catch (removeError) {
      setChecked(true);
      toast({
        variant: 'destructive',
        title: t('ACTION_FAILED') || 'Action failed',
        description: serverErrorText(removeError) || t('SOMETHING_WENT_WRONG') || 'Something went wrong.',
      });
    }
  };

  const showFields = mustEnter || checked;

  return (
    <div className="flex flex-col gap-3" data-testid="own-tool-client">
      {mustEnter ? (
        <p className="text-sm text-muted-foreground">
          {t('OWN_TOOL_CLIENT_REQUIRED') ||
            'This tool signs you in through your own app client. Enter its client ID, and its client secret if it has one.'}
        </p>
      ) : (
        <div className="flex items-center gap-2">
          <Checkbox
            id={`own-client-${toolId}`}
            checked={checked}
            disabled={isRemoving}
            onCheckedChange={(value) => void toggle(value === true)}
          />
          <Label htmlFor={`own-client-${toolId}`} className="cursor-pointer text-sm">
            {t('USE_OWN_TOOL_CLIENT') || 'Use my own client instead of the configured one'}
          </Label>
        </div>
      )}

      {showFields && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor={`own-client-id-${toolId}`} className="text-sm font-medium leading-6">
              {t('CLIENT_ID') || 'Client ID'}
              <span className="text-destructive ml-0.5">*</span>
            </Label>
            <Input
              id={`own-client-id-${toolId}`}
              className="h-10"
              autoComplete="off"
              placeholder={t('ENTER_CLIENT_ID') || 'Enter client ID'}
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
            />
            {error && <span className="text-xs text-destructive">{error}</span>}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`own-client-secret-${toolId}`} className="text-sm font-medium leading-6">
              {t('CLIENT_SECRET_IF_ANY') || 'Client Secret (if your app has one)'}
            </Label>
            <Input
              id={`own-client-secret-${toolId}`}
              type="password"
              className="h-10"
              autoComplete="new-password"
              placeholder={
                connection?.ownClientSecretSaved ? '••••••••' : t('ENTER_CLIENT_SECRET') || 'Enter client secret'
              }
              value={clientSecret}
              onChange={(event) => setClientSecret(event.target.value)}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {t('OWN_TOOL_CLIENT_HINT') ||
              'Your own app registration at the identity provider. Saved for you only and never shared with the model.'}
          </p>
          <div>
            <Button type="button" variant="outline" onClick={() => void save()} disabled={isSaving}>
              {isSaving ? t('SAVING') || 'Saving...' : t('SAVE_CLIENT') || 'Save client'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default OwnToolClientSection;
